import type { GridApi } from 'ag-grid-community';
import { cloneDeep, get, isEqual, mapValues, noop, set } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { map } from 'rxjs';
import { v4 as uuid } from 'uuid';
import type { EntityAdminClass } from '.';
import { useUserContext } from '../../contexts';
import { useCallbackRef, useDisclosure, useDisclosureWithContext, useGlobalToasts } from '../../hooks';
import { AppwideDrawerContentType, useAppwideDrawerContext } from '../../providers';
import { DELETE, EMPTY_ARRAY, logger, PATCH, POST, PUT, request, type Paths } from '../../utils';
import { HStack } from '../Core';
import { IconName } from '../Icons';
import { NotificationVariants } from '../Notification';
import type { EntityAdminConfirmationDialogContext } from './components/EntityAdminConfirmationDialog';
import { EntityAdminDrawerDataProvider, useIsInsideEntityAdminDrawer } from './components/EntityAdminDrawerContext';
import { EntityAdminDrawerHeader } from './components/EntityAdminDrawerHeader';
import { getEntityAdminGuideHeaderContent } from './components/EntityAdminGuideToggle';
import { useEntityAdminHeaderLayout } from './components/EntityAdminHeaderLayoutContext';
import type {
  InputsAndDropdownsDrawerOption,
  InputsAndDropdownsDrawerOptions,
} from './components/InputsAndDropdownsDrawer/types';
import { isDrawerOptionsObservable, readDrawerOptions } from './drawerOptionsSource';
import { ENTITY_INTERNAL_ROW_ID, GLOBAL_ENTITY_INTERNAL_ID } from './EntityAdminClass';
import { getEntityAdminBulkConfirmReason, getEntityAdminTypeLabel } from './getEntityAdminBulkConfirmReason';
import type { EntityAdminBulkUploadParams, EntityAdminBulkUploadRow } from './getEntityAdminBulkUploadRows';
import { getEntityAdminEditDisplayName } from './getEntityAdminEditDisplayName';
import { useDrawerSamenessCheck } from './hooks/useDrawerSamenessCheck';
import type {
  EntityAdminBlotterTableApi,
  EntityAdminDrawerOptionsSource,
  EntityAdminModeSwitchConfiguration,
  EntityAdminOperationErrorParams,
  EntityAdminOperationSettledParams,
  EntityAdminOperationSuccessParams,
  EntityAdminPanelAction,
  EntityAdminRecord,
  HierarchicalColumnProps,
  UseEntityAdminProps,
} from './types';
import { handleCustomSaveEntity } from './useEntityAdminCustomSave';
import { handleEntityOperationError, handleEntityOperationSuccess } from './useEntityAdminResponseHandlers';
import { bulkEditQueue, delay, extractFieldsFromDrawerOptions, getEntityRowId, removeHiddenFields } from './utils';
import { useEntityAdminTabsContext } from './wrappers/EntityAdminTabsWrapper';

export function useEntityAdmin<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord = TRecord>(
  props: UseEntityAdminProps<TRecord, TDrawerRecord>
) {
  const { add: addToast, remove: removeToast } = useGlobalToasts();
  const tabsContext = useEntityAdminTabsContext();
  const filtersPortalId = useMemo(() => `entity-admin-filters-${uuid()}` as const, []);

  /** We need this to trigger blotter updates that propagate from the drawer */
  const blotterTableApiRef = useRef<EntityAdminBlotterTableApi<TRecord>>({
    // These references are uninitialized at first, but guaranteed to be set by the time we use them.
    gridApi: { isDestroyed: () => true } as GridApi<EntityAdminClass<TRecord>>,
    updateRecord: noop,
    refresh: noop,
    ensureRowVisibleById: noop,
  });

  const {
    entityName = 'Entity',
    childEntityName,
    // If tabsContext is not null, set useTabs to false so that we don't re-add tabs wrapper
    useTabs = props.persistKey != null && tabsContext == null,
    sendEmptyStringForNulledValues = false,
    removeNullValuesFromPatchPayload = false,
    postEntityInArray = false,
    usePutForPatchPost = false,
    shouldUsePatchForPost = (e: TDrawerRecord) => false,
    entityUpdateMethod = 'PATCH',
    bulkEditFields = EMPTY_ARRAY,
    confirmTextActions = EMPTY_ARRAY,
    maxBulkEditRows = 10_000,
    globalEntity,
    getEntityForPatchPost: propsGetEntityForPatchPost,
    scrollToEntityAfterSave = false,
    onOperationSuccess,
    onOperationSettled,
    shouldCloseDrawerOnSuccess,
  } = props;

  const isInsideEntityAdminDrawer = useIsInsideEntityAdminDrawer();
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const suppressAutoDrawerActions = isInsideEntityAdminDrawer;
  const suppressHeader = isInsideEntityAdminDrawer;

  const { openDrawer, closeDrawer, isOpen: isAppwideDrawerOpen, contentSpec } = useAppwideDrawerContext();
  const drawerOwnerIdRef = useRef(uuid());
  const isEntityAdminDrawerContent = contentSpec?.type === AppwideDrawerContentType.EntityAdmin;
  // True for every mounted EntityAdmin while any EntityAdmin drawer is open. Selection uses this so a
  // checkbox on a second panel can claim the drawer.
  const isEntityAdminDrawerOpen = isAppwideDrawerOpen && isEntityAdminDrawerContent;
  const isOwnedEntityAdminDrawerOpen = isEntityAdminDrawerOpen && contentSpec?.ownerId === drawerOwnerIdRef.current;
  const contentSpecRef = useRef(contentSpec);
  const [isEntityAdminDrawerDirty, setIsEntityAdminDrawerDirty] = useState(false);
  const isDirtyRef = useRef(isEntityAdminDrawerDirty);
  isDirtyRef.current = isEntityAdminDrawerDirty;

  const requestCloseDrawer = useCallbackRef(() => {
    if (isDirtyRef.current && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    closeDrawer();
  });
  const modeSwitchConfigurationInternal = useMemo((): EntityAdminModeSwitchConfiguration<TRecord> | undefined => {
    const defaultModeField = 'Mode' as Paths<TRecord>;
    if (props.allowModeSwitch === true) {
      return { modeField: defaultModeField };
    }
    if (props.allowModeSwitch === false || props.allowModeSwitch == null) {
      return undefined;
    }
    return {
      ...props.allowModeSwitch,
      modeField: props.allowModeSwitch.modeField ?? defaultModeField,
    };
  }, [props.allowModeSwitch]);

  // Live AG Grid row selection — always updated when grid selection changes (bulk edit and bulk select).
  // Used for row highlighting and bulk-select workflows; not always what the drawer is editing.
  const [selectedEntities, setSelectedEntities] = useState<EntityAdminClass<TRecord>[]>([]);
  // Snapshot of entities the open drawer is editing. Set when the drawer opens; in allowBulkEdit mode it
  // stays in sync with grid selection, but in allowBulkSelect mode grid multi-select does not change it.
  const [drawerEditEntities, setDrawerEditEntities] = useState<EntityAdminClass<TRecord>[]>([]);
  const [isBulkRequesting, setIsBulkRequesting] = useState(false);
  const [isBulkUploadReady, setIsBulkUploadReady] = useState(false);
  const bulkUploadDialog = useDisclosure();
  const parentMapRef = useRef<Map<keyof TRecord | typeof GLOBAL_ENTITY_INTERNAL_ID, TRecord>>(new Map());
  const loadedEntitiesRef = useRef<ReadonlyMap<string, EntityAdminClass<TRecord>>>(new Map());
  const { shouldOpenDrawer } = useDrawerSamenessCheck<TRecord>(isOwnedEntityAdminDrawerOpen);

  const lastAddingChildEntityRef = useRef(false);
  const drawerOpenedWithEmptyOptionsRef = useRef(false);

  useEffect(() => {
    contentSpecRef.current = contentSpec;
  }, [contentSpec]);

  const hasOwnedEntityAdminDrawerOpen = useCallback(() => {
    const currentContentSpec = contentSpecRef.current;
    return (
      currentContentSpec?.type === AppwideDrawerContentType.EntityAdmin &&
      currentContentSpec.ownerId === drawerOwnerIdRef.current
    );
  }, []);

  // When the globalEntity prop changes, update parentMapRef's GLOBAL_ENTITY_INTERNAL_ID
  useEffect(() => {
    if (globalEntity != null) {
      // The globalEntity does not have an entityIDField or childIDField, but it is still a valid TRecord
      parentMapRef.current.set(GLOBAL_ENTITY_INTERNAL_ID, globalEntity as TRecord);
    } else {
      parentMapRef.current.delete(GLOBAL_ENTITY_INTERNAL_ID);
    }
    // refresh the grid so the GLOBAL_ENTITY_INTERNAL_ID is taken into account by the objects
    if (!blotterTableApiRef.current.gridApi.isDestroyed()) {
      blotterTableApiRef.current.gridApi.refreshCells();
    }
  }, [globalEntity]);

  const updateSelectedEntities = useCallbackRef((selectedClasses: EntityAdminClass<TRecord>[]) => {
    setSelectedEntities(selectedClasses);
  });

  /** Manage the Confirmation Dialog internals here.
   * The EntityAdminConfirmationDialog component is used to render the dialog. */
  const confirmDialog = useDisclosureWithContext<EntityAdminConfirmationDialogContext>();

  const getPostPath = useMemo(
    () => props.getPostPath ?? ((_: TDrawerRecord) => props.path!),
    [props.getPostPath, props.path]
  );
  const getPatchPath = useMemo(
    () =>
      props.getPatchPath ??
      ((entity: TDrawerRecord) => `${getPostPath(entity)}/${encodeURIComponent(get(entity, props.entityIDField!))}`),
    [props.getPatchPath, getPostPath, props.entityIDField]
  );
  const getDeletePath = useMemo(() => props.getDeletePath ?? getPatchPath, [props.getDeletePath, getPatchPath]);

  const getEntityAsDrawerEntity = useCallbackRef(
    props.getEntityAsDrawerEntity ?? ((entity: TRecord) => entity as unknown as TDrawerRecord)
  );
  const getClassEntityEditDisplayName = (classEntity: EntityAdminClass<TRecord>) => {
    const drawerEntity = getEntityAsDrawerEntity(classEntity.data);
    return getEntityAdminEditDisplayName({
      entity: classEntity.data,
      entityIDField: props.entityIDField,
      childIDField: props.childIDField,
      isChildRow: classEntity.isChildRow,
      entityDisplayName: props.getEntityDisplayName?.(drawerEntity),
      childEntityDisplayName: props.getChildEntityDisplayName?.(drawerEntity),
    });
  };
  const getResponseAsBlotterEntity = useMemo(
    () => props.getResponseAsBlotterEntity ?? ((response: unknown) => response as TRecord),
    [props.getResponseAsBlotterEntity]
  );
  const getEntityDrawerOptions = useMemo(
    () => props.getEntityDrawerOptions ?? (() => props.drawerOptions),
    [props.getEntityDrawerOptions, props.drawerOptions]
  );

  const calculateEntityValueChecks = useCallback(
    (selectedEntities: EntityAdminClass<TRecord>[], addingChildEntity: boolean) => {
      if (selectedEntities.length <= 1) {
        return new Map<keyof TDrawerRecord, boolean>();
      }

      const firstEntity = selectedEntities.at(0);
      if (!firstEntity) {
        return new Map<keyof TDrawerRecord, boolean>();
      }

      const valueChecks = new Map<keyof TDrawerRecord, boolean>();
      const drawerOptions = readDrawerOptions(
        getEntityDrawerOptions(getEntityAsDrawerEntity(firstEntity.data), addingChildEntity)
      );
      const allFields = extractFieldsFromDrawerOptions(drawerOptions ?? []);

      // Pre-calculate field value comparisons for all drawer options
      allFields.forEach(field => {
        const firstValue = get(firstEntity.data, field);
        const hasMultipleValues = selectedEntities.some(entity => !isEqual(get(entity.data, field), firstValue));
        valueChecks.set(field, hasMultipleValues);
      });

      return valueChecks;
    },
    [getEntityDrawerOptions, getEntityAsDrawerEntity]
  );

  const getSingleEntityDrawerOptions = useCallback(
    (selectedEntities: EntityAdminClass<TRecord>[], addingChildEntity = false) => {
      const firstEntity = selectedEntities.at(0);
      return getEntityDrawerOptions(
        firstEntity?.data ? getEntityAsDrawerEntity(firstEntity.data) : undefined,
        addingChildEntity
      );
    },
    [getEntityAsDrawerEntity, getEntityDrawerOptions]
  );

  const mapToBulkEditDrawerOptions = useCallback(
    (
      drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord> | undefined,
      selectedEntities: EntityAdminClass<TRecord>[],
      addingChildEntity: boolean
    ) => {
      if (drawerOptions == null || selectedEntities.length <= 1) {
        return drawerOptions;
      }

      const entityValueChecks = calculateEntityValueChecks(selectedEntities, addingChildEntity);
      const mapOption = (option: InputsAndDropdownsDrawerOption<TDrawerRecord>) => {
        if (option.type === 'divider') {
          return option;
        }

        // Use pre-calculated value check for better performance
        const hasMultipleValues = entityValueChecks.get(option.field) ?? false;

        return {
          ...option,
          ...(selectedEntities.length > 1 && {
            placeholder: hasMultipleValues ? 'Multiple Values' : option.placeholder,
            getIsDisabled: () => !bulkEditFields.includes(option.field),
            getIsRequired: (form: TDrawerRecord) => (option.getIsRequired?.(form) ?? false) && !hasMultipleValues,
          }),
        };
      };

      return drawerOptions.map(optionOrGroup =>
        Array.isArray(optionOrGroup) ? optionOrGroup.map(mapOption) : mapOption(optionOrGroup)
      );
    },
    [bulkEditFields, calculateEntityValueChecks]
  );

  const getBulkEditDrawerOptions = useCallback(
    (selectedEntities: EntityAdminClass<TRecord>[], addingChildEntity = false) => {
      return mapToBulkEditDrawerOptions(
        readDrawerOptions(getSingleEntityDrawerOptions(selectedEntities, addingChildEntity)),
        selectedEntities,
        addingChildEntity
      );
    },
    [getSingleEntityDrawerOptions, mapToBulkEditDrawerOptions]
  );

  const getBulkEditDrawerOptionsInput = useCallback(
    (selectedEntities: EntityAdminClass<TRecord>[], addingChildEntity = false) => {
      const source = getSingleEntityDrawerOptions(selectedEntities, addingChildEntity);
      if (selectedEntities.length <= 1 || source == null || !isDrawerOptionsObservable(source)) {
        return selectedEntities.length <= 1 ? source : getBulkEditDrawerOptions(selectedEntities, addingChildEntity);
      }

      return source.pipe(map(options => mapToBulkEditDrawerOptions(options, selectedEntities, addingChildEntity)));
    },
    [getBulkEditDrawerOptions, getSingleEntityDrawerOptions, mapToBulkEditDrawerOptions]
  );

  // Derived from drawerEditEntities (not selectedEntities) so bulk-select grid selection does not reshape
  // the open drawer's fields until the user explicitly opens edit.
  const drawerOptionsSource = useMemo(
    () => getSingleEntityDrawerOptions(drawerEditEntities),
    [drawerEditEntities, getSingleEntityDrawerOptions]
  );
  const drawerOptionsVersion = useDrawerOptionsVersion(drawerOptionsSource);
  const drawerOptions = useMemo(() => {
    // Observable sources keep a stable identity, so this version is what refreshes the snapshot.
    void drawerOptionsVersion;
    return getBulkEditDrawerOptions(drawerEditEntities);
  }, [getBulkEditDrawerOptions, drawerEditEntities, drawerOptionsVersion]);

  const fieldsWeCareAbout = useMemo(() => {
    return [
      ...extractFieldsFromDrawerOptions(drawerOptions ?? EMPTY_ARRAY),
      ...(modeSwitchConfigurationInternal != null ? [modeSwitchConfigurationInternal.modeField] : EMPTY_ARRAY),
    ];
  }, [drawerOptions, modeSwitchConfigurationInternal]);

  const applyFinalPayloadTransformations = useCallback(
    (value: unknown) => {
      if (value === '') {
        return sendEmptyStringForNulledValues ? '' : removeNullValuesFromPatchPayload ? undefined : null;
      }
      return value;
    },
    [sendEmptyStringForNulledValues, removeNullValuesFromPatchPayload]
  );

  const getEntityName = useCallbackRef(
    ({
      setupEntities,
      addingChildEntity,
    }: {
      setupEntities: EntityAdminClass<TRecord>[];
      addingChildEntity: boolean;
    }): string => {
      if (addingChildEntity) {
        return `New ${childEntityName ?? entityName}`;
      }
      if (setupEntities?.length) {
        const setupEntityIsChild = setupEntities[0].isChildRow;
        if (setupEntities.length === 1) {
          const displayName = getClassEntityEditDisplayName(setupEntities[0]);
          if (displayName) {
            return displayName;
          }
        }
        if (setupEntityIsChild && childEntityName) {
          return childEntityName;
        }
        return entityName;
      }
      return `New ${entityName}`;
    }
  );

  const buildDrawerContentSpec = useCallbackRef(
    (setupEntities: EntityAdminClass<TRecord>[], addingChildEntity: boolean) => {
      const entityDrawerOptions = getBulkEditDrawerOptions(setupEntities, addingChildEntity);
      const drawerOptionsInput = getBulkEditDrawerOptionsInput(setupEntities, addingChildEntity);
      const drawerTitle = getEntityName({ setupEntities, addingChildEntity });
      const renderGuideHeaderContent = getEntityAdminGuideHeaderContent(
        entityDrawerOptions,
        props.showGuideToggle === true
      );

      return {
        type: AppwideDrawerContentType.EntityAdmin,
        ownerId: drawerOwnerIdRef.current,
        persistOnNavigate: isPanelLayout,
        onClose: requestCloseDrawer,
        drawerHeaderDesign: 'flex' as const,
        flexHeaderTitle: () => (
          <EntityAdminDrawerHeader
            drawerTitle={drawerTitle}
            selectedCount={setupEntities.length}
            addingChildEntity={addingChildEntity}
          />
        ),
        ...((renderGuideHeaderContent != null || setupEntities.length > 1) && {
          renderHeaderContent: renderGuideHeaderContent ?? (() => <HStack gap="spacing16" alignItems="center" />),
        }),
        renderContent: () => (
          <EntityAdminDrawerDataProvider<TRecord>
            value={{
              gridApi: blotterTableApiRef.current.gridApi,
              entityIDField: props.entityIDField as string | undefined,
              childIDField: props.childIDField as string | undefined,
              addingChildEntity,
              parentEntity: addingChildEntity ? setupEntities.at(0) : undefined,
            }}
          >
            {props.renderDrawer({
              drawerOptions: drawerOptionsInput ?? EMPTY_ARRAY,
              selectedEntities: setupEntities,
              onSaveEntities: async (entityClasses, newEntity, context) => {
                const { parentEntity } = context;

                if (props.onCustomSaveEntity) {
                  const entityForCustomSave = cloneDeep(newEntity) as TDrawerRecord;
                  // Use `entityDrawerOptions` here, not the outer `fieldsWeCareAbout`: the latter is derived from the
                  // grid selection and misses child-row fields when the drawer edits a different entity.
                  // TODO: unify both save paths on a single drawer-derived field list.
                  const customSaveFields = [
                    ...extractFieldsFromDrawerOptions(entityDrawerOptions ?? EMPTY_ARRAY),
                    ...(modeSwitchConfigurationInternal != null
                      ? [modeSwitchConfigurationInternal.modeField]
                      : EMPTY_ARRAY),
                  ];
                  customSaveFields.forEach(field => {
                    const value = get(entityForCustomSave, field);
                    if (value != null) {
                      set(entityForCustomSave, field, applyFinalPayloadTransformations(value));
                    }
                  });
                  const customSaveHandled = await handleCustomSaveEntity({
                    onCustomSaveEntity: props.onCustomSaveEntity,
                    newEntity: entityForCustomSave,
                    entityClasses,
                    parentEntity,
                    addingChildEntity,
                    entityName,
                    childEntityName,
                    getEntityAsDrawerEntity,
                    handleOperationSuccess,
                    handleOperationSettled,
                    handleOperationError,
                    ensureRowVisibleById: blotterTableApiRef.current.ensureRowVisibleById,
                  });
                  if (customSaveHandled) {
                    return;
                  }
                }

                // Default save behavior
                return entityClasses == null
                  ? createEntityWithConfirmation(newEntity)
                  : updateClassEntitiesWithConfirmation(entityClasses, newEntity);
              },
              addingChildEntity,
              getEntityAsDrawerEntity: getEntityAsDrawerEntity,
              setIsEntityAdminDrawerDirty,
              suppressEditingFooterActions: props.suppressEditingFooterActions,
              suppressDrawerFormSubmit: props.suppressDrawerFormSubmit,
            })}
          </EntityAdminDrawerDataProvider>
        ),
      };
    }
  );

  const openEntityAdminDrawer: HierarchicalColumnProps<TRecord>['openEntityAdminDrawer'] = useCallbackRef(
    ({ setupEntities, addingChildEntity }) => {
      if (isBulkRequesting) {
        addToast({ text: `Please wait for the current request to finish.`, variant: NotificationVariants.Warning });
        return;
      }

      if (suppressAutoDrawerActions) {
        return;
      }

      if (!shouldOpenDrawer(setupEntities, addingChildEntity)) {
        return;
      }

      lastAddingChildEntityRef.current = addingChildEntity;
      const entityDrawerOptions = getBulkEditDrawerOptions(setupEntities, addingChildEntity);
      // No form is mounted while the snapshot is empty, so the first populated emission re-opens the drawer
      // and builds the header from those options. Later emissions update the mounted form in place.
      drawerOpenedWithEmptyOptionsRef.current = entityDrawerOptions == null || entityDrawerOptions.length === 0;

      setDrawerEditEntities(setupEntities);
      openDrawer(buildDrawerContentSpec(setupEntities, addingChildEntity));
    }
  );

  const closeEntityAdminDrawer = useCallback(() => {
    if (suppressAutoDrawerActions) {
      // Auto drawer actions are suppressed, so we don't need to close the drawer
      return;
    }
    // We can only close EADrawers, other drawers should not be closed by EA.
    if (!hasOwnedEntityAdminDrawerOpen()) {
      return;
    }
    closeDrawer();
  }, [closeDrawer, hasOwnedEntityAdminDrawerOpen, suppressAutoDrawerActions]);

  // Another panel claimed the drawer. Clear this grid so only the owning panel stays checked.
  // Nested admins and allowBulkSelect grids do not represent the drawer owner.
  useEffect(() => {
    if (suppressAutoDrawerActions || props.allowBulkSelect) {
      return;
    }
    if (!isEntityAdminDrawerOpen || isOwnedEntityAdminDrawerOpen) {
      return;
    }
    const gridApi = blotterTableApiRef.current.gridApi;
    if (gridApi.isDestroyed() || gridApi.getSelectedRows().length === 0) {
      return;
    }
    gridApi.deselectAll();
  }, [isEntityAdminDrawerOpen, isOwnedEntityAdminDrawerOpen, props.allowBulkSelect, suppressAutoDrawerActions]);

  // Re-open the drawer when drawerOptions transition from empty to populated while open.
  // This handles the case where the drawer is opened before its options data is available.
  useEffect(() => {
    if (!isOwnedEntityAdminDrawerOpen || suppressAutoDrawerActions) {
      return;
    }
    if (!drawerOpenedWithEmptyOptionsRef.current) {
      return;
    }
    if (drawerOptions == null || drawerOptions.length === 0) {
      return;
    }

    drawerOpenedWithEmptyOptionsRef.current = false;
    openDrawer(buildDrawerContentSpec(drawerEditEntities, lastAddingChildEntityRef.current));
  }, [
    drawerOptions,
    isOwnedEntityAdminDrawerOpen,
    suppressAutoDrawerActions,
    openDrawer,
    buildDrawerContentSpec,
    drawerEditEntities,
  ]);

  const handleOperationSettled = useCallback(
    (params: EntityAdminOperationSettledParams) => {
      onOperationSettled?.(params);
    },
    [onOperationSettled]
  );

  const handleOperationSuccess = useCallback(
    (params: EntityAdminOperationSuccessParams) => {
      handleEntityOperationSuccess({
        ...params,
        addToast,
        closeEntityAdminDrawer,
        shouldCloseDrawer: shouldCloseDrawerOnSuccess?.(params) !== false,
      });
      onOperationSuccess?.(params);
    },
    [addToast, closeEntityAdminDrawer, onOperationSuccess, shouldCloseDrawerOnSuccess]
  );

  const handleOperationError = useCallback(
    (params: EntityAdminOperationErrorParams) => {
      handleEntityOperationError({
        ...params,
        addToast,
      });
    },
    [addToast]
  );

  const { orgApiEndpoint } = useUserContext();
  const getPathWithApiEndpoint = useCallbackRef((path: string) => `${props.baseUrl ?? orgApiEndpoint}${path}`);

  const getEntityForPatchPost = useMemo(
    () => (entity: TDrawerRecord) => {
      const entityWithHiddenFieldsRemoved = removeHiddenFields<TDrawerRecord>(entity, drawerOptions ?? EMPTY_ARRAY);
      return propsGetEntityForPatchPost
        ? (propsGetEntityForPatchPost(entityWithHiddenFieldsRemoved) as TDrawerRecord)
        : entityWithHiddenFieldsRemoved;
    },
    [drawerOptions, propsGetEntityForPatchPost]
  );

  const postEntity = useCallbackRef((entity: TDrawerRecord) =>
    request<{ data: TRecord[] }>(
      usePutForPatchPost ? PUT : shouldUsePatchForPost(entity) ? PATCH : POST,
      getPathWithApiEndpoint(getPostPath(entity)),
      postEntityInArray ? [getEntityForPatchPost(entity)] : getEntityForPatchPost(entity)
    )
  );
  const patchEntity = useCallbackRef((entity: TDrawerRecord) =>
    request<{ data: TRecord[] }>(
      usePutForPatchPost ? PUT : PATCH,
      getPathWithApiEndpoint(getPatchPath(entity)),
      getEntityForPatchPost(entity)
    )
  );
  const deleteEntity = useCallbackRef((entity: TDrawerRecord) =>
    request<void>(DELETE, getPathWithApiEndpoint(getDeletePath(entity)))
  );

  const handleOnCreateNewEntity = useCallbackRef(async (newEntity: TDrawerRecord) => {
    return postEntity(newEntity)
      .then(({ data }) => {
        const response = data.at(0);
        if (!response) {
          logger.error(
            new Error(`No data returned from the server after creating new ${entityName}. Grid not updated.`),
            { entityName: entityName, createdEntity: newEntity }
          );
          return undefined;
        }
        const blotterEntity = getResponseAsBlotterEntity(response);
        blotterTableApiRef.current.updateRecord(blotterEntity, 'add');

        if (scrollToEntityAfterSave) {
          const rowId = getEntityRowId(blotterEntity, props.entityIDField, props.childIDField);
          blotterTableApiRef.current.ensureRowVisibleById(rowId, { highlight: true });
        }

        handleOperationSuccess({
          action: 'create',
          isChildEntity: false,
          entityName,
          childEntityName,
        });
        handleOperationSettled({
          action: 'create',
          isChildEntity: false,
          entityName,
          childEntityName,
        });

        return response;
      })
      .catch(error => {
        handleOperationError({
          action: 'create',
          isChildEntity: false,
          entityName,
          childEntityName,
          error,
        });
        handleOperationSettled({
          action: 'create',
          isChildEntity: false,
          entityName,
          childEntityName,
        });
      });
  });

  const updateClassEntity = useCallbackRef(
    async (
      classEntity: EntityAdminClass<TRecord>,
      newEntity: EntityAdminRecord,
      showSuccessMsg = true,
      showErrorMsg = true,
      fieldsToApply: readonly PropertyKey[] = fieldsWeCareAbout
    ): Promise<EntityAdminRecord | undefined> => {
      const entityForPatch = getEntityAsDrawerEntity(cloneDeep(classEntity.data));
      fieldsToApply.forEach(field => {
        const value = get(newEntity, field);
        if (value != null) {
          set(entityForPatch, field, applyFinalPayloadTransformations(value));
        }
      });

      const entityUpdate = entityUpdateMethod === 'POST' ? postEntity : patchEntity;
      return entityUpdate(entityForPatch)
        .then(async ({ data }) => {
          const response = data.at(0);
          if (!response) {
            logger.error(
              new Error(`No data returned from the server after updating ${entityName}. Grid not updated.`),
              { entityName: entityName, entity: entityForPatch }
            );
            return undefined;
          }

          const processedResponse: TRecord = props.parseResponseValues
            ? // [UI-6253] If parseResponseValues is true, we parse the response values to get the correct TRecord type
              mapValues(response, value => {
                if (typeof value === 'string' && value.trim()[0] === '{') {
                  try {
                    return JSON.parse(value);
                  } catch (error) {
                    logger.warn(`Failed to parse JSON: ${error}`, {
                      entityName: entityName,
                      value: value,
                    });
                  }
                }
                return value;
              })
            : response; // If parseResponseValues is false, the response is already TRecord

          const blotterEntity = getResponseAsBlotterEntity(processedResponse);
          const primaryEntity = blotterEntity;

          blotterTableApiRef.current.updateRecord(blotterEntity, 'update');

          const rowNode = blotterTableApiRef.current.gridApi.getRowNode(classEntity[ENTITY_INTERNAL_ROW_ID]);
          if (rowNode == null) {
            logger.error(new Error(`Row node not found for ${entityName} after updating. Grid not updated.`), {
              entityName: entityName,
              entity: entityForPatch,
            });
            return undefined;
          }
          // When a parent row updates, we must update the parent map ref.
          // Child rows access parent data via proxy, so they automatically see updated parent values.
          if (classEntity.isParentRow && classEntity.parentMapRef.current != null && primaryEntity) {
            const entityId = get(primaryEntity, classEntity.entityIDField);
            if (entityId != null) {
              classEntity.parentMapRef.current.set(entityId, primaryEntity);
            }
            // Refresh child rows so they see updated parent values via proxy
            const childrenRowNodes = rowNode.allLeafChildren;
            if (childrenRowNodes?.length) {
              blotterTableApiRef.current.gridApi.refreshCells({ rowNodes: childrenRowNodes });
            }
          }

          if (showSuccessMsg) {
            if (props.refetchOnUpdate) {
              blotterTableApiRef.current.refresh();
            }
            await delay(250); // Allow the grid api's getSelectedRows to update.
            updateSelectedEntities(blotterTableApiRef.current.gridApi.getSelectedRows());

            if (scrollToEntityAfterSave) {
              blotterTableApiRef.current.ensureRowVisibleById(classEntity[ENTITY_INTERNAL_ROW_ID], {
                highlight: true,
              });
            }

            handleOperationSuccess({
              action: 'save',
              isChildEntity: classEntity.isChildRow ?? false,
              entityName,
              childEntityName,
            });
            handleOperationSettled({
              action: 'save',
              isChildEntity: classEntity.isChildRow ?? false,
              entityName,
              childEntityName,
            });
          } else {
            // Still close drawer even if not showing success message
            closeEntityAdminDrawer();
          }

          return response;
        })
        .catch(error => {
          if (showErrorMsg) {
            handleOperationError({
              action: 'save',
              isChildEntity: classEntity.isChildRow ?? false,
              entityName,
              childEntityName,
              error,
            });
          }
          if (showSuccessMsg) {
            handleOperationSettled({
              action: 'save',
              isChildEntity: classEntity.isChildRow ?? false,
              entityName,
              childEntityName,
            });
          }

          throw error;
        });
    }
  );
  const deleteClassEntity = useCallbackRef(async (classEntity: EntityAdminClass<TRecord>) => {
    const drawerEntity = getEntityAsDrawerEntity(classEntity.data);
    const runDelete = props.onCustomDeleteEntity
      ? () => Promise.resolve().then(() => props.onCustomDeleteEntity?.(drawerEntity))
      : () => deleteEntity(drawerEntity);

    return runDelete()
      .then(() => {
        if (classEntity.isParentRow) {
          classEntity.getAllChildren(blotterTableApiRef.current.gridApi).forEach(entityClass => {
            // Remove any children of the parent record first.
            blotterTableApiRef.current.updateRecord(entityClass.data, 'remove');
          });
        }
        blotterTableApiRef.current.updateRecord(classEntity.data, 'remove');
        if (classEntity.isParentRow && classEntity.parentMapRef.current != null) {
          const entityId = classEntity.data[props.entityIDField];
          if (entityId != null) {
            classEntity.parentMapRef.current.delete(entityId);
          }
        }
        handleOperationSuccess({
          action: 'delete',
          isChildEntity: classEntity.isChildRow ?? false,
          entityName,
          childEntityName,
        });
        handleOperationSettled({
          action: 'delete',
          isChildEntity: classEntity.isChildRow ?? false,
          entityName,
          childEntityName,
        });
        return;
      })
      .catch(error => {
        handleOperationError({
          action: 'delete',
          isChildEntity: classEntity.isChildRow ?? false,
          entityName,
          childEntityName,
          error,
        });
        handleOperationSettled({
          action: 'delete',
          isChildEntity: classEntity.isChildRow ?? false,
          entityName,
          childEntityName,
        });
      });
  });

  const createEntityWithConfirmation = useCallbackRef(async (newEntity: TDrawerRecord) => {
    if (isBulkRequesting) {
      addToast({ text: `Please wait for the current request to finish.`, variant: NotificationVariants.Warning });
      return;
    }
    if (confirmTextActions.includes('single-add')) {
      confirmDialog.open({
        ConfirmReason: `Are you sure you want to create this ${entityName}?`,
        RequiresConfirmText: true,
        ConfirmAction: () => handleOnCreateNewEntity(newEntity),
      });
    } else {
      await handleOnCreateNewEntity(newEntity);
    }
  });

  const onConfirmBulkEditEntities = useCallbackRef(
    async (dirtyEntities: EntityAdminClass<TRecord>[], newEntity: EntityAdminRecord) => {
      closeEntityAdminDrawer();
      confirmDialog.close();
      setIsBulkRequesting(true);
      await bulkEditQueue(dirtyEntities, newEntity, updateClassEntity, addToast, removeToast);
      if (props.refetchOnUpdate) {
        blotterTableApiRef.current.refresh();
      }
      await delay(250); // Allow the grid api's getSelectedRows to update.
      updateSelectedEntities(blotterTableApiRef.current.gridApi.getSelectedRows());
      setIsBulkRequesting(false);
      handleOperationSettled({
        action: 'save',
        isChildEntity: dirtyEntities[0]?.isChildRow ?? false,
        entityName,
        childEntityName,
      });
    }
  );

  /*
   * Internal only - we export updateClassEntitiesWithConfirmation instead.
   */
  const updateClassEntityWithConfirmation = useCallbackRef(
    async (classEntity: EntityAdminClass<TRecord>, newEntity: EntityAdminRecord) => {
      if (isEqual(classEntity.data, newEntity)) {
        addToast({
          text: `No changes made to ${entityName}.`,
          variant: NotificationVariants.Warning,
        });
        return;
      }
      if (confirmTextActions.includes('single-edit')) {
        confirmDialog.open({
          ConfirmReason: `Are you sure you want to save changes to this ${getEntityAdminTypeLabel({
            isChildRow: classEntity.isChildRow,
            entityName,
            childEntityName,
          })}?`,
          RequiresConfirmText: true,
          ConfirmAction: async () => {
            await updateClassEntity(classEntity, newEntity);
          },
        });
      } else {
        await updateClassEntity(classEntity, newEntity);
      }
    }
  );
  const updateClassEntitiesWithConfirmation = useCallbackRef(
    async (classEntities: EntityAdminClass<TRecord>[], newEntity: EntityAdminRecord) => {
      if (isBulkRequesting) {
        addToast({ text: `Please wait for the current request to finish.`, variant: NotificationVariants.Warning });
        return;
      }
      const dirtyClassEntities = classEntities.filter(entityClass => {
        const drawerEntity = getEntityAsDrawerEntity(entityClass.data, entityClass);
        return fieldsWeCareAbout
          .filter(field => get(newEntity, field) != null)
          .some(field => !isEqual(get(drawerEntity, field), get(newEntity, field)));
      });
      if (dirtyClassEntities.length === 1) {
        await updateClassEntityWithConfirmation(dirtyClassEntities[0], newEntity);
      } else if (dirtyClassEntities.length > 1) {
        confirmDialog.open({
          ConfirmReason: getEntityAdminBulkConfirmReason({
            action: 'edit',
            count: dirtyClassEntities.length,
            entityName,
            childEntityName,
            isChildRow: dirtyClassEntities[0]?.isChildRow === true,
          }),
          RequiresConfirmText: confirmTextActions.includes('bulk-edit'),
          ConfirmAction: () => onConfirmBulkEditEntities(dirtyClassEntities, newEntity),
        });
      } else if (classEntities.length === 1) {
        addToast({
          text: `No changes made to ${entityName}.`,
          variant: NotificationVariants.Warning,
        });
      }
    }
  );

  /*
   * Internal only - we export deleteClassEntitiesWithConfirmation instead.
   */
  const deleteClassEntityWithConfirmation = useCallbackRef(async (classEntity: EntityAdminClass<TRecord>) => {
    const nameToUse = getEntityAdminTypeLabel({
      isChildRow: classEntity.isChildRow,
      entityName,
      childEntityName,
    });

    const displayName = getClassEntityEditDisplayName(classEntity);
    const hasValidDisplayName = displayName != null;

    const confirmReason = hasValidDisplayName
      ? `Are you sure you want to delete ${nameToUse} '${displayName}'?`
      : `Are you sure you want to delete this ${nameToUse}?`;

    confirmDialog.open({
      ConfirmReason: confirmReason,
      ConfirmDetails: props.getDeleteConfirmDetails?.([classEntity.data]),
      RequiresConfirmText: confirmTextActions.includes('single-delete'),
      ConfirmAction: () => deleteClassEntity(classEntity),
    });
  });
  const deleteClassEntitiesWithConfirmation = useCallbackRef(async (classEntities: EntityAdminClass<TRecord>[]) => {
    if (isBulkRequesting) {
      addToast({ text: `Please wait for the current request to finish.`, variant: NotificationVariants.Warning });
      return;
    }
    if (classEntities.length === 1) {
      deleteClassEntityWithConfirmation(classEntities[0]);
    } else if (classEntities.length > 1) {
      confirmDialog.open({
        ConfirmReason: getEntityAdminBulkConfirmReason({
          action: 'delete',
          count: classEntities.length,
          entityName,
          childEntityName,
          isChildRow: classEntities[0]?.isChildRow === true,
        }),
        ConfirmDetails: props.getDeleteConfirmDetails?.(classEntities.map(entity => entity.data)),
        RequiresConfirmText: confirmTextActions.includes('bulk-delete'),
        ConfirmAction: async () => {
          confirmDialog.close();
          setIsBulkRequesting(true);
          try {
            await Promise.all(classEntities.map(entity => deleteClassEntity(entity)));
          } finally {
            setIsBulkRequesting(false);
          }
        },
      });
    }
  });

  const getBulkUploadParams = useCallbackRef(
    (): EntityAdminBulkUploadParams<TRecord, TDrawerRecord> => ({
      entityIDField: props.entityIDField,
      childIDField: props.childIDField,
      newEntityDefault: props.newEntityDefault,
      loadedEntities: loadedEntitiesRef.current,
      getDrawerOptions: (entity, addingChildEntity) =>
        readDrawerOptions(getEntityDrawerOptions(entity, addingChildEntity)) ?? EMPTY_ARRAY,
      getEntityAsDrawerEntity,
    })
  );

  const createBulkUploadEntity = useCallbackRef(async (newEntity: TDrawerRecord, showErrorMsg: boolean) => {
    try {
      const { data } = await postEntity(newEntity);
      const response = data.at(0);
      if (response) {
        blotterTableApiRef.current.updateRecord(getResponseAsBlotterEntity(response), 'add');
      }
      return response;
    } catch (error) {
      if (showErrorMsg) {
        handleOperationError({
          action: 'create',
          isChildEntity: props.childIDField != null && get(newEntity, props.childIDField) != null,
          entityName,
          childEntityName,
          error,
        });
      }
      throw error;
    }
  });

  const bulkUploadEntities = useCallbackRef(async (rows: EntityAdminBulkUploadRow<TRecord, TDrawerRecord>[]) => {
    setIsBulkRequesting(true);
    await bulkEditQueue(
      rows,
      {},
      (row, _newEntity, _showSuccessMsg, showErrorMsg = true) => {
        if (row.existingEntity == null) {
          return createBulkUploadEntity(row.entity, showErrorMsg);
        }
        // The preview can go stale, so patch the latest entity to keep live values in fields the CSV omits
        const latestEntity =
          loadedEntitiesRef.current.get(row.existingEntity[ENTITY_INTERNAL_ROW_ID]) ?? row.existingEntity;
        return updateClassEntity(latestEntity, row.entity, false, showErrorMsg, row.providedFields);
      },
      addToast,
      removeToast
    );
    if (props.refetchOnUpdate) {
      blotterTableApiRef.current.refresh();
    }
    setIsBulkRequesting(false);
  });

  const panelActions = useMemo((): EntityAdminPanelAction[] | undefined => {
    if (!props.allowBulkUpload) {
      return props.panelActions;
    }
    return [
      ...(props.panelActions ?? EMPTY_ARRAY),
      {
        label: 'Upload',
        startIcon: IconName.Upload,
        disabled: drawerOptions == null || isBulkRequesting || !isBulkUploadReady,
        onClick: bulkUploadDialog.open,
        testId: 'entity-admin-page-upload-button',
      },
    ];
  }, [
    bulkUploadDialog.open,
    drawerOptions,
    isBulkRequesting,
    isBulkUploadReady,
    props.allowBulkUpload,
    props.panelActions,
  ]);

  const value = useMemo(() => {
    return {
      ...props,
      getEntityAsDrawerEntity,
      useTabs,
      selectedEntities,
      openEntityAdminDrawer,
      closeEntityAdminDrawer,
      drawerOptions,
      blotterTableApiRef,
      isEntityAdminDrawerOpen,
      isOwnedEntityAdminDrawerOpen,
      confirmDialog,
      maxBulkEditRows,
      updateClassEntitiesWithConfirmation,
      deleteClassEntitiesWithConfirmation,
      isRequesting: isBulkRequesting,
      updateSelectedEntities,
      parentMapRef,
      isEntityAdminDrawerDirty,
      setIsEntityAdminDrawerDirty,
      modeSwitchConfigurationInternal,
      suppressHeader,
      filtersPortalId,
      panelActions,
      bulkUploadDialog,
      getBulkUploadParams,
      bulkUploadEntities,
      loadedEntitiesRef,
      setIsBulkUploadReady,
    };
  }, [
    closeEntityAdminDrawer,
    confirmDialog,
    deleteClassEntitiesWithConfirmation,
    drawerOptions,
    getEntityAsDrawerEntity,
    isBulkRequesting,
    isEntityAdminDrawerOpen,
    isOwnedEntityAdminDrawerOpen,
    maxBulkEditRows,
    openEntityAdminDrawer,
    props,
    selectedEntities,
    updateClassEntitiesWithConfirmation,
    updateSelectedEntities,
    useTabs,
    parentMapRef,
    isEntityAdminDrawerDirty,
    modeSwitchConfigurationInternal,
    suppressHeader,
    filtersPortalId,
    panelActions,
    bulkUploadDialog,
    getBulkUploadParams,
    bulkUploadEntities,
  ]);

  return value;
}

/** Re-render when a stable observable emits a new drawer options snapshot. */
function useDrawerOptionsVersion<TDrawerRecord extends EntityAdminRecord>(
  source: EntityAdminDrawerOptionsSource<TDrawerRecord> | null | undefined
) {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!isDrawerOptionsObservable(source)) {
      return;
    }
    const subscription = source.subscribe(() => {
      setVersion(current => current + 1);
    });
    return () => subscription.unsubscribe();
  }, [source]);
  return version;
}
